import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Shield, Users, AlertTriangle } from 'lucide-react';

const TermsPage: React.FC = () => {
  const lastUpdated = "January 1, 2025";

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="mb-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-purple-400 hover:text-purple-300 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          
          <h1 className="text-4xl font-bold text-white mb-4">Terms of Service</h1>
          
          <div className="flex items-center text-gray-400 text-sm mb-6">
            <Calendar className="w-4 h-4 mr-2" />
            <span>Last updated: {lastUpdated}</span>
          </div>
          
          <div className="bg-purple-900 bg-opacity-30 border border-purple-500 rounded-lg p-4 mb-8">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-purple-400 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-purple-200 text-sm">
                  <strong>Important:</strong> By reading and using JustJam, you automatically accept these terms of service. 
                  If you do not agree with these terms, please do not use our service.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="prose prose-invert max-w-none">
          
          {/* Section 1 */}
          <section className="mb-8">
            <div className="flex items-center mb-4">
              <Shield className="w-6 h-6 text-purple-500 mr-3" />
              <h2 className="text-2xl font-bold text-white">1. Acceptance of Terms</h2>
            </div>
            <div className="text-gray-300 space-y-4">
              <p>
                Welcome to JustJam ("we", "our" or "the Service"). These Terms of Service ("Terms") 
                govern the use of our website and services provided by JustJam.
              </p>
              <p>
                By accessing or using our Service, you agree to be bound by these Terms. 
                If you do not agree with any part of these terms, you may not access the Service.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section className="mb-8">
            <div className="flex items-center mb-4">
              <Users className="w-6 h-6 text-purple-500 mr-3" />
              <h2 className="text-2xl font-bold text-white">2. Description of the Service</h2>
            </div>
            <div className="text-gray-300 space-y-4">
              <p>
                JustJam is a free platform that allows musicians to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Create personal musical profiles</li>
                <li>Search for other musicians in their geographical area</li>
                <li>Connect and communicate with other users</li>
                <li>Organize jam sessions and musical collaborations</li>
              </ul>
              <p>
                The service is currently provided free of charge and we reserve the right to modify 
                or discontinue the service at any time.
              </p>
            </div>
          </section>

          {/* Section 3 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">3. Registration and User Account</h2>
            <div className="text-gray-300 space-y-4">
              <p>
                To use some features of the Service, you must register an account. You agree to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide accurate, complete and up-to-date information</li>
                <li>Maintain the security of your password</li>
                <li>Be responsible for all activities that occur under your account</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
              </ul>
              <p>
                You must be at least 18 years old to use this service. If you are under 18, 
                you must have the consent of your parents or legal guardians.
              </p>
            </div>
          </section>

          {/* Section 4 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">4. User Conduct</h2>
            <div className="text-gray-300 space-y-4">
              <p>
                By using JustJam, you agree NOT to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Post offensive, defamatory, illegal or inappropriate content</li>
                <li>Harass, intimidate or threaten other users</li>
                <li>Use the service for unauthorized commercial purposes</li>
                <li>Create false accounts or impersonate other people</li>
                <li>Attempt to illegally access systems or data of other users</li>
                <li>Use bots, scripts or other automated means to access the service</li>
                <li>Post spam or unsolicited content</li>
              </ul>
              <p>
                We reserve the right to suspend or terminate your account if you violate these terms.
              </p>
            </div>
          </section>

          {/* Section 5 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">5. Content and Intellectual Property</h2>
            <div className="text-gray-300 space-y-4">
              <p>
                You retain all rights to the content you post on JustJam. However, 
                by granting us a limited license to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Display your content on the platform</li>
                <li>Allow other users to view and interact with your content</li>
                <li>Use the content to improve the service</li>
              </ul>
              <p>
                You are responsible for ensuring you have all necessary rights to the content you post.
              </p>
            </div>
          </section>

          {/* Section 6 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">6. Privacy and Data Protection</h2>
            <div className="text-gray-300 space-y-4">
              <p>
                Your privacy is important to us. We collect and use your personal information 
                in accordance with our Privacy Policy and the GDPR.
              </p>
              <p>
                The information you share publicly (such as your musical profile) 
                will be visible to other users of the platform.
              </p>
            </div>
          </section>

          {/* Section 7 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">7. Limitation of Liability</h2>
            <div className="text-gray-300 space-y-4">
              <p>
                JustJam is provided "as is" without warranties of any kind. We are not responsible for:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Service interruptions or data loss</li>
                <li>Interactions between users outside the platform</li>
                <li>Content posted by other users</li>
                <li>Direct, indirect, incidental or consequential damages</li>
                <li>Security issues resulting from misuse of the service</li>
              </ul>
              <p>
                <strong>Important:</strong> Meetings with other users occur at your own risk. 
                We advise you to always meet new people in public and safe places.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">8. Changes to the Service</h2>
            <div className="text-gray-300 space-y-4">
              <p>
                We reserve the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Modify or discontinue the service at any time</li>
                <li>Update these terms of service</li>
                <li>Introduce paid features in the future</li>
                <li>Remove content that violates our terms</li>
              </ul>
              <p>
                We will notify you of significant changes via email or platform notice.
              </p>
            </div>
          </section>

          {/* Section 9 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">9. Termination</h2>
            <div className="text-gray-300 space-y-4">
              <p>
                You can terminate your account at any time by deleting your profile. 
                We may terminate or suspend your account if:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>You violate these terms of service</li>
                <li>Your behavior harms other users or the platform</li>
                <li>It is required by law</li>
              </ul>
              <p>
                Upon termination, your right to use the service will cease immediately.
              </p>
            </div>
          </section>

          {/* Section 10 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">10. Governing Law</h2>
            <div className="text-gray-300 space-y-4">
              <p>
                These terms are governed by Italian laws. Any dispute 
                will be subject to the exclusive jurisdiction of the Italian courts.
              </p>
            </div>
          </section>

          {/* Section 11 */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">11. Contacts</h2>
            <div className="text-gray-300 space-y-4">
              <p>
                For questions regarding these Terms of Service, you can contact us at:
              </p>
              <div className="bg-gray-800 rounded-lg p-4 mt-4">
                <p><strong>Email:</strong> info@justjam.it</p>
              </div>
            </div>
          </section>

          {/* Footer */}
          <div className="border-t border-gray-700 pt-8 mt-12">
            <div className="bg-gray-800 rounded-lg p-6">
              <h3 className="text-lg font-bold text-white mb-3">Important Summary</h3>
              <div className="text-gray-300 text-sm space-y-2">
                <p>• JustJam is a free service to connect musicians</p>
                <p>• You must be at least 16 years old to use the service</p>
                <p>• You are responsible for your content and behavior</p>
                <p>• Meetings with other users occur at your own risk</p>
                <p>• We reserve the right to modify or terminate the service</p>
                <p>• These terms are subject to Italian laws</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default TermsPage;
